import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { useAuth } from '../../contexts/AuthContext';
import RoleBasedNavigation from '../../components/navigation/RoleBasedNavigation';
import Icon from '../../components/AppIcon';

const PrivacyPolicy = () => {
  const { user, userProfile, isAuthenticated } = useAuth();
  const [activeSection, setActiveSection] = useState(null);

  const sections = [
    {
      id: 'information-collection',
      title: '1. Information We Collect',
      content: [
        {
          subtitle: 'Account Information',
          text: 'When you register for BardPlay, we collect basic information including your name, email address, role (student/educator/admin), and school affiliation. This information is necessary to create and manage your account.'
        },
        {
          subtitle: 'Usage Data',
          text: 'We automatically collect information about your interactions with our platform, including pages visited, features used, quiz responses, reading progress, and time spent on activities. This helps us improve your learning experience and platform functionality.'
        },
        {
          subtitle: 'Educational Progress',
          text: 'We track your learning progress, including completed plays, quiz scores, achievements earned, and glossary terms studied. This data helps provide personalized recommendations and track academic growth.'
        }
      ]
    },
    {
      id: 'information-use',
      title: '2. How We Use Your Information',
      content: [
        {
          subtitle: 'Educational Services',
          text: 'We use your information to provide personalized learning experiences, track academic progress, generate performance analytics, and facilitate educator-student interactions within the platform.'
        },
        {
          subtitle: 'Platform Improvement',
          text: 'Your usage data helps us understand how students interact with Shakespeare content, identify areas for improvement, and develop new features that enhance learning outcomes.'
        },
        {
          subtitle: 'Communication',
          text: 'We may send you important updates about your account, new features, educational resources, and platform notifications. You can control these preferences in your account settings.'
        }
      ]
    },
    {
      id: 'data-sharing',
      title: '3. Information Sharing and Disclosure',
      content: [
        {
          subtitle: 'Educators and Schools',
          text: 'If you\'re a student, your progress data and performance metrics are shared with authorized educators and administrators at your institution for academic purposes.'
        },
        {
          subtitle: 'Service Providers',
          text: 'We work with trusted service providers who help us operate our platform, including hosting services, analytics tools, and authentication providers. These partners are bound by strict confidentiality agreements.'
        },
        {
          subtitle: 'Legal Requirements',
          text: 'We may disclose your information if required by law, legal process, or to protect the rights, property, or safety of BardPlay, our users, or the public.'
        }
      ]
    },
    {
      id: 'data-security',
      title: '4. Data Security',
      content: [
        {
          subtitle: 'Protection Measures',
          text: 'We implement industry-standard security measures including encryption, secure authentication, regular security audits, and access controls to protect your personal information from unauthorized access, alteration, or destruction.'
        },
        {
          subtitle: 'Data Retention',
          text: 'We retain your account information and educational data for as long as your account is active or as needed to provide services. You can request account deletion at any time, after which your data will be securely removed from our systems.'
        }
      ]
    },
    {
      id: 'user-rights',
      title: '5. Your Rights and Choices',
      content: [
        {
          subtitle: 'Access and Control',
          text: 'You have the right to access, update, or delete your personal information at any time through your account settings. You can also export your learning data and progress records.'
        },
        {
          subtitle: 'Privacy Settings',
          text: 'Control your privacy preferences including notification settings, data sharing with educators, and visibility of your achievements and progress to other users.'
        },
        {
          subtitle: 'Account Deletion',
          text: 'You may request permanent deletion of your account and associated data. Please note that some information may be retained for legal compliance or legitimate business purposes.'
        }
      ]
    },
    {
      id: 'cookies',
      title: '6. Cookies and Tracking Technologies',
      content: [
        {
          subtitle: 'Essential Cookies',
          text: 'We use necessary cookies to maintain your session, remember your preferences, and ensure platform security. These are required for basic functionality.'
        },
        {
          subtitle: 'Analytics Cookies',
          text: 'With your consent, we use analytics cookies to understand how users interact with our platform, helping us improve features and user experience.'
        }
      ]
    },
    {
      id: 'children-privacy',
      title: '7. Children\'s Privacy',
      content: [
        {
          subtitle: 'COPPA Compliance',
          text: 'BardPlay is designed for educational use and may be used by students under 13 with appropriate parental or school consent. We comply with the Children\'s Online Privacy Protection Act (COPPA) and obtain proper consent before collecting information from children.'
        },
        {
          subtitle: 'Parental Rights',
          text: 'Parents and guardians have the right to review, modify, or delete their child\'s information. Schools and educators act in loco parentis for students using BardPlay in educational settings.'
        }
      ]
    },
    {
      id: 'policy-changes',
      title: '8. Changes to This Policy',
      content: [
        {
          subtitle: 'Updates and Notifications',
          text: 'We may update this privacy policy to reflect changes in our practices or legal requirements. We will notify you of significant changes via email or prominent platform notice. Continued use of BardPlay after changes constitutes acceptance of the updated policy.'
        },
        {
          subtitle: 'Effective Date',
          text: 'This privacy policy is effective as of December 12, 2025. Last updated: December 12, 2025.'
        }
      ]
    },
    {
      id: 'contact',
      title: '9. Contact Us',
      content: [
        {
          subtitle: 'Privacy Questions',
          text: 'If you have questions about this privacy policy, your data, or our privacy practices, please contact us at privacy@bardplay.com or through our contact page. We respond to all privacy inquiries within 48 hours.'
        }
      ]
    }
  ];

  const toggleSection = (sectionId) => {
    setActiveSection(activeSection === sectionId ? null : sectionId);
  };

  return (
    <>
      <Helmet>
        <title>Privacy Policy - BardPlay</title>
        <meta name="description" content="BardPlay's privacy policy explains how we collect, use, and protect your personal information while providing educational services." />
      </Helmet>
      
      <div className="min-h-screen bg-gradient-to-br from-background via-surface-dark to-background">
        <RoleBasedNavigation 
          user={{
            ...user,
            role: userProfile?.role,
            full_name: userProfile?.full_name,
            email: user?.email
          }} 
          isAuthenticated={isAuthenticated} 
        />
        
        <main className="main-content pt-24 pb-16">
          {/* Header */}
          <div className="max-w-4xl mx-auto px-6 lg:px-8 mb-12">
            <div className="text-center">
              <div className="inline-flex items-center gap-2 bg-primary/10 px-4 py-2 rounded-full mb-6">
                <Icon name="Shield" size={20} className="text-primary" />
                <span className="text-primary font-semibold">Your Privacy Matters</span>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">Privacy Policy</h1>
              <p className="text-lg text-white/70">
                Last Updated: December 12, 2025
              </p>
              <p className="text-white/60 mt-4 max-w-2xl mx-auto">
                At BardPlay, we are committed to protecting your privacy and ensuring the security of your personal information. 
                This policy explains how we collect, use, and safeguard your data.
              </p>
            </div>
          </div>

          {/* Privacy Policy Content */}
          <div className="max-w-4xl mx-auto px-6 lg:px-8">
            <div className="bg-surface-dark/50 backdrop-blur-sm rounded-xl border border-white/10 overflow-hidden">
              {sections?.map((section, index) => (
                <div key={section?.id} className={`border-b border-white/10 ${index === sections?.length - 1 ? 'border-b-0' : ''}`}>
                  <button
                    onClick={() => toggleSection(section?.id)}
                    className="w-full px-6 py-5 flex items-center justify-between hover:bg-white/5 transition-colors duration-200"
                  >
                    <h2 className="text-xl font-bold text-white text-left">{section?.title}</h2>
                    <Icon 
                      name={activeSection === section?.id ? 'ChevronUp' : 'ChevronDown'} 
                      size={24} 
                      className="text-white/60 flex-shrink-0"
                    />
                  </button>
                  
                  {activeSection === section?.id && (
                    <div className="px-6 pb-6 space-y-6">
                      {section?.content?.map((item, idx) => (
                        <div key={idx}>
                          <h3 className="text-lg font-semibold text-white mb-2">{item?.subtitle}</h3>
                          <p className="text-white/70 leading-relaxed">{item?.text}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Quick Summary */}
            <div className="mt-12 bg-gradient-to-r from-primary/10 to-secondary/10 rounded-xl p-6 border border-white/10">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                  <Icon name="Info" size={24} className="text-primary" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white mb-3">Privacy at a Glance</h3>
                  <ul className="space-y-2 text-white/70">
                    <li className="flex items-start gap-2">
                      <Icon name="Check" size={20} className="text-primary flex-shrink-0 mt-0.5" />
                      <span>We only collect information necessary for educational services</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Icon name="Check" size={20} className="text-primary flex-shrink-0 mt-0.5" />
                      <span>Your data is never sold to third parties</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Icon name="Check" size={20} className="text-primary flex-shrink-0 mt-0.5" />
                      <span>You have full control over your information and privacy settings</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Icon name="Check" size={20} className="text-primary flex-shrink-0 mt-0.5" />
                      <span>We use industry-standard security measures to protect your data</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </>
  );
};

export default PrivacyPolicy;